import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Link2, Copy, Eye, Loader2, Trash2, Linkedin } from "lucide-react";
import api from "@/lib/api";

/** "Shareable Resume Link" — one persistent public link per user (always reflects their current
 * saved draft) plus a lightweight view-count. Used in both the Wizard's Review step and the
 * Advanced editor's sidebar. */
export default function ShareResumeCard() {
  const [share, setShare] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/resume-builder/share/analytics").then(({ data }) => setShare(data)).catch(() => {});
  }, []);

  const createLink = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/resume-builder/share");
      setShare(data);
      toast.success("Shareable link created");
    } catch {
      toast.error("Could not create a shareable link right now");
    } finally {
      setLoading(false);
    }
  };

  const revokeLink = async () => {
    setLoading(true);
    try {
      await api.delete("/resume-builder/share");
      setShare({ share_id: null, view_count: 0 });
      toast.success("Link revoked — it will no longer work");
    } catch {
      toast.error("Could not revoke the link right now");
    } finally {
      setLoading(false);
    }
  };

  const shareUrl = share?.share_id ? `${window.location.origin}/r/${share.share_id}` : "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Could not copy automatically — please select and copy the link manually");
    }
  };

  return (
    <div className="glass-panel rounded-2xl p-4" data-testid="resume-share-card">
      <h3 className="flex items-center gap-2 text-sm font-heading font-semibold text-white mb-2">
        <Link2 className="w-4 h-4 text-indigo-400" /> Shareable Resume Link
      </h3>
      {!share?.share_id ? (
        <button
          onClick={createLink} disabled={loading} data-testid="resume-create-share-link-button"
          className="flex items-center justify-center gap-1.5 w-full bg-indigo-500 hover:bg-indigo-400 disabled:opacity-60 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />} Get Shareable Link
        </button>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <input readOnly value={shareUrl} data-testid="resume-share-link-input" className="flex-1 bg-white/5 border border-white/10 rounded-lg px-2.5 py-1.5 text-[11px] text-slate-300 truncate" />
            <button onClick={copyLink} data-testid="resume-copy-share-link-button" className="p-1.5 rounded-lg bg-white/10 hover:bg-white/15 text-white"><Copy className="w-3.5 h-3.5" /></button>
            <a
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noreferrer"
              data-testid="resume-share-linkedin-button" className="p-1.5 rounded-lg bg-[#0A66C2]/15 hover:bg-[#0A66C2]/25 text-[#5DA1F1]"
            >
              <Linkedin className="w-3.5 h-3.5" />
            </a>
          </div>
          <div className="flex items-center justify-between text-[11px] text-slate-400">
            <span className="flex items-center gap-1" data-testid="resume-share-view-count"><Eye className="w-3 h-3" /> {share.view_count} view{share.view_count === 1 ? "" : "s"}</span>
            <button onClick={revokeLink} disabled={loading} data-testid="resume-revoke-share-link-button" className="flex items-center gap-1 text-rose-400 hover:text-rose-300 disabled:opacity-60">
              <Trash2 className="w-3 h-3" /> Revoke
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
